import type { ColumnConfigEntry } from '@erp/shared-types';

/** Saved views of the buyer board's Orders table (user and org default). */
export const BUYER_BOARD_ORDERS_TABLE_ID = 'procurement-buyer-board-orders';
/** The transfer board's own saved Orders views (GEN-3026). */
export const TRANSFER_BOARD_ORDERS_TABLE_ID =
  'procurement-transfer-board-orders';

type Translate = (message: string, options?: Record<string, unknown>) => string;

// Today's Orders table: who the order is with, then its money, then status.
const ORDER_COLUMN_IDS: readonly string[] = [
  'vendor',
  'shipTo',
  'buyer',
  'backOrders',
  'potf',
  'stock',
  'sharing',
  'lines',
  'core',
  'value',
  'poMinimum',
  'prepaid',
  'readiness',
  'queued',
  'inboundMode',
  'oldestSo',
];

// Transfer-only columns are blank on a PO row.
const HIDDEN_ON_PO: ReadonlySet<string> = new Set(['queued', 'inboundMode']);
// A transfer has no vendor minimum, prepaid freight or readiness check, and no
// buy a transfer could cover.
const HIDDEN_ON_TO: ReadonlySet<string> = new Set([
  'sharing',
  'poMinimum',
  'prepaid',
  'readiness',
]);

// Money, counts and dates read right-aligned, as in a spreadsheet.
const RIGHT_ALIGNED_ORDER_COLUMN_IDS: ReadonlySet<string> = new Set([
  'backOrders',
  'potf',
  'stock',
  'sharing',
  'lines',
  'core',
  'value',
  'poMinimum',
  'prepaid',
  'queued',
  'oldestSo',
]);

function orderColumnTitles(gt: Translate): Record<string, string> {
  return {
    vendor: gt('From', {
      context: 'buying orders column header, the vendor or source location',
    }),
    shipTo: gt('Ship-to', { context: 'buying orders column header' }),
    buyer: gt('Buyer', {
      context: 'buying orders column header, buyer code',
    }),
    backOrders: gt('BO $', {
      context: 'buying orders column header, back order dollars',
    }),
    // Rexel's name for its non-stock sales-order lines (GEN-3151); the
    // column id stays `potf` so saved views keep their place.
    potf: gt('PTFS $', {
      context:
        'buying orders column header, PTFS (product on the fly) sales-order line dollars',
    }),
    stock: gt('Stock $', {
      context: 'buying orders column header, stock dollars',
    }),
    sharing: gt('Sharing $', {
      context:
        'buying orders column header, dollars of the buy that a transfer could cover instead (sharing suggestions)',
    }),
    lines: gt('Lines', { context: 'buying orders column header' }),
    core: gt('Core', {
      context:
        'buying orders column header, count of lines in a core-offer program',
    }),
    value: gt('PO Value', {
      context: 'buying orders column header, cart total',
    }),
    poMinimum: gt('PO min', {
      context:
        'buying orders column header, the vendor minimum order value for this ship-to',
    }),
    prepaid: gt('Prepaid', {
      context:
        'buying orders column header, the vendor prepaid-freight threshold for this ship-to',
    }),
    readiness: gt('Order', {
      context:
        'buying orders column header: can the PO be placed, and is it the best order',
    }),
    queued: gt('Queued', {
      context:
        'buying orders column header, transfer lines the daily auto-release limit held back',
    }),
    inboundMode: gt('Inbound', {
      context:
        'buying orders column header, whether the destination stages transfers automatically or reviews them',
    }),
    oldestSo: gt('Oldest SO', {
      context: 'buying orders column header, date of the oldest sales order',
    }),
  };
}

/**
 * The standard Orders columns. The Orders table sorts on the server by one
 * column at a time, so these carry no saved sort: a header click still sorts.
 */
export function buyerBoardOrderDefaultColumns(
  gt: Translate,
  kind: 'po' | 'to' = 'po',
): ColumnConfigEntry[] {
  const titles = orderColumnTitles(gt);
  const hidden = kind === 'po' ? HIDDEN_ON_PO : HIDDEN_ON_TO;
  return ORDER_COLUMN_IDS.map((id) => ({
    id,
    apiField: '',
    header: titles[id] ?? id,
    alignRight: RIGHT_ALIGNED_ORDER_COLUMN_IDS.has(id),
    sortable: false,
    ...(hidden.has(id) ? { hidden: true } : {}),
  }));
}
